import { readFeedRaw, viewErrorFrom } from '@services/feedView';
import { SPECULATIVE_COLUMNS, isPayloadName } from '@features/feedView/columns';
import { fetchPayloadFields } from '@features/feedView/specPayloadFields';
import { COPY, FEED_INPUT_MAX_COMPANY_IDS, FEED_INPUT_MAX_ROWS, entryBytes, toFeedInput } from './feedInputs';

// One Attach: the ~24-second read of one feed, and what to ask it for.
//
// What is asked for follows the Feed View page's own rules (useColumnNegotiation, buildColumnsParam),
// reduced to the two cases an attachment has:
//
//   payload off  -> slim: true,  columns: the floor plus sector / market_cap / company_name, the
//                   three names every feed's universe carries (feed_view.py's PIT columns).
//   payload on   -> slim: false, columns: those three plus the spec's declared payload names. A
//                   payload column is emptied under slim and only arrives when `columns` names it,
//                   so both halves are needed.
//
// The payload names come from the feed's score spec (specPayloadFields.js: one GET, before the
// read) — but only when the caller holds the grant that route is gated on, because the service
// runs handle400Statuses and a 403 is never to be attempted-and-caught. Without the grant, or when
// the spec declares nothing, the page's A1 bootstrap applies: read once, and if the response's
// `available_columns` names payload columns the rows do not carry, read once more naming them. Two
// reads, paid only with the checkbox on and no spec to ask.
//
// Index membership flags are not requested: their names are only knowable from a prior response,
// and the attachment's size budget was drawn without them.
//
// Returns one of:
//   { item, droppedColumns, columnsRefused }   a FeedInputItem ready for form state
//   { error, retryable }                        the server said no, in its own words where it had any
//   { aborted: true }                           the caller cancelled
export const fetchFeedInput = async ({
  feedId,
  knowableFrom = null,
  knowableTo = null,
  companies = [],
  includePayload = false,
  canReadSpecs = false,
  signal,
  now,
}) => {
  const companyIds = (companies || []).map((company) => company.company_id);
  if (companyIds.length > FEED_INPUT_MAX_COMPANY_IDS) {
    return { error: COPY.tooManyCompanies, retryable: false };
  }

  let payloadNames = null;
  if (includePayload && canReadSpecs) {
    const { resolved } = await fetchPayloadFields(feedId);
    payloadNames = Array.isArray(resolved) && resolved.length ? resolved : null;
  }

  const request = (columns) =>
    readFeedRaw({
      feedId,
      knowableFrom,
      knowableTo,
      companyIds,
      slim: !includePayload,
      columns,
      signal,
    });

  let read = await request([...SPECULATIVE_COLUMNS, ...(payloadNames || [])]);
  if (read.response.aborted) return { aborted: true };

  // The bootstrap: the spec could not be asked, the server has now named the payload columns this
  // feed carries, and the rows came without them. Once, and only when there is something to gain.
  if (includePayload && !payloadNames && read.response.status === 200 && !read.droppedColumns) {
    const data = read.response.data || {};
    const firstRow = Array.isArray(data.rows) && data.rows.length ? data.rows[0] : null;
    const universe = Array.isArray(data.available_columns) ? data.available_columns : [];
    const missing = firstRow ? universe.filter(isPayloadName).filter((name) => !(name in firstRow)) : [];
    if (missing.length) {
      const again = await request([...SPECULATIVE_COLUMNS, ...missing]);
      if (again.response.aborted) return { aborted: true };
      // A refusal on the second read is not a failure of the first: keep what arrived. That
      // includes a refusal readFeedRaw rescued by retrying without `columns` — the rows came back,
      // but floor-only, and trading the enriched first answer for them would lose sector, market
      // cap and company name and then blame the server for refusing columns it had just accepted.
      if (again.response.status === 200 && again.response.data && !again.droppedColumns) read = again;
    }
  }

  const { status, data, error } = read.response;
  if (status !== 200 || !data) {
    return {
      error: viewErrorFrom(data, error, status),
      retryable: status === 503 || status === 0 || status >= 500,
    };
  }

  if (Array.isArray(data.rows) && data.rows.length > FEED_INPUT_MAX_ROWS) {
    return { error: COPY.tooManyRows(data.rows.length), retryable: false };
  }

  const built = toFeedInput(data, read.body, now ? { now } : undefined);
  if (built.error) return { error: built.error, retryable: false };

  const { entry, droppedColumns } = built;
  return {
    item: { id: entry.feed_id, entry, bytes: entryBytes(entry), companies: companies || [] },
    droppedColumns,
    // The server refused the `columns` key and the rows are the floor alone: no sector, market cap
    // or company name. Worth a note on the chip; not a reason to refuse the attachment.
    columnsRefused: !!read.droppedColumns,
  };
};
